import { Cookies } from 'react-cookie';

const COOKIE_STORAGE_KEYS = [
  'accessToken',
  'appToken',
  'refreshToken',
  'expiryTime',
  'vapistxId',
];

interface CookieOptions {
  path?: string;
  domain?: string;
  maxAge?: number;
  expires?: Date;
  secure?: boolean;
  sameSite?: 'lax' | 'strict' | 'none';
}

interface CookieKeyValue {
  key: string;
  value: string;
}

class CookiesManager {
  private cookies: Cookies;

  constructor() {
    this.cookies = new Cookies();
  }

  set(key: string, value: string, options: CookieOptions = {}) {
    if (COOKIE_STORAGE_KEYS.includes(key)) {
      const cookieOptions = {
        path: options.path || '/',
        domain: options.domain,
        maxAge: options.maxAge,
        expires: options.expires,
        secure: options.secure,
        sameSite: options.sameSite,
      };

      // Remove undefined values
      Object.keys(cookieOptions).forEach((key) => {
        if (cookieOptions[key as keyof typeof cookieOptions] === undefined) {
          delete cookieOptions[key as keyof typeof cookieOptions];
        }
      });

      this.cookies.set(key, value, cookieOptions);
    } else {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, value);
      }
    }
  }

  setMultiple(cookieList: CookieKeyValue[], options?: CookieOptions) {
    cookieList.forEach(({ key, value }) => {
      this.set(key, value, options);
    });
  }

  get(key: string): string | undefined {
    if (COOKIE_STORAGE_KEYS.includes(key)) {
      const value = this.cookies.get(key);
      return value || undefined;
    } else {
      if (typeof window !== 'undefined') {
        return localStorage.getItem(key) || undefined;
      }
      return undefined;
    }
  }

  getAll(): Record<string, string> {
    const storage: Record<string, string> = {};

    // Get all cookies
    const allCookies = this.cookies.getAll();
    COOKIE_STORAGE_KEYS.forEach((key) => {
      if (allCookies[key]) {
        storage[key] = allCookies[key];
      }
    });

    // Get localStorage items
    if (typeof window !== 'undefined') {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !COOKIE_STORAGE_KEYS.includes(key)) {
          const value = localStorage.getItem(key);
          if (value) {
            storage[key] = value;
          }
        }
      }
    }

    return storage;
  }

  remove(
    key: string,
    options: Omit<CookieOptions, 'maxAge' | 'expires'> = {},
  ): void {
    if (COOKIE_STORAGE_KEYS.includes(key)) {
      const removeOptions = {
        path: options.path || '/',
        domain: options.domain,
        secure: options.secure,
        sameSite: options.sameSite,
      };

      // Remove undefined values
      Object.keys(removeOptions).forEach((optKey) => {
        if (removeOptions[optKey as keyof typeof removeOptions] === undefined) {
          delete removeOptions[optKey as keyof typeof removeOptions];
        }
      });

      this.cookies.remove(key, removeOptions);
    } else {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(key);
      }
    }
  }

  deleteAll(options?: Omit<CookieOptions, 'maxAge' | 'expires'>) {
    COOKIE_STORAGE_KEYS.forEach((key) => {
      this.remove(key, options);
    });

    if (typeof window !== 'undefined') {
      localStorage.clear();
    }
  }
}

// Create a singleton instance
const cookiesManager = new CookiesManager();

export default cookiesManager;
